import { useCallback } from 'react';

import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDispatchUndoSnack } from 'src/helpers';

export interface RelationshipForMutation {
  childRelationshipType: Apollo.ChildRelationshipType;
  id: string;
  parentDocumentId: string | null;
  review: {
    outcome: Apollo.ReviewOutcome;
    reviewedBy: { __typename: 'User'; displayName: string; id: string } | null;
  } | null;
  source: Apollo.DocumentRelationshipSource;
  childDocumentId?: string | null;
}

function buildReviewedBy(currentUser: { displayName: string; id: string } | null | undefined) {
  if (!currentUser) return null;
  return { __typename: 'User' as const, displayName: currentUser.displayName, id: currentUser.id };
}

export function useConfirmRelationship() {
  const currentUser = useSelectCurrentUser();
  const [confirmRelationship, { loading }] = Apollo.useConfirmDocumentRelationshipMutation();

  const confirm = useCallback(
    async (rel: RelationshipForMutation) => {
      const reviewedBy = buildReviewedBy(currentUser);
      await confirmRelationship({
        optimisticResponse: reviewedBy
          ? {
              __typename: 'Mutation' as const,
              confirmDocumentRelationship: {
                __typename: 'DocumentRelationship' as const,
                childDocumentId: rel.childDocumentId ?? null,
                childRelationshipType: rel.childRelationshipType,
                id: rel.id,
                parentDocumentId: rel.parentDocumentId ?? null,
                review: {
                  __typename: 'DocumentRelationshipReview' as const,
                  outcome: Apollo.ReviewOutcome.Confirmed,
                  reviewedBy,
                },
                source: rel.source,
              },
            }
          : undefined,
        variables: { id: rel.id },
      });
    },
    [confirmRelationship, currentUser]
  );

  return { confirm, loading };
}

export function useRejectRelationship() {
  const [rejectRelationship, { loading }] = Apollo.useRejectDocumentRelationshipMutation();
  const [restoreRelationship] = Apollo.useRestoreDocumentRelationshipMutation();
  const currentUser = useSelectCurrentUser();
  const dispatchUndoSnack = useDispatchUndoSnack();

  const reject = useCallback(
    async (rel: RelationshipForMutation, options: { documentId: string; showUndo?: boolean }) => {
      const { documentId, showUndo = true } = options;
      const { data } = await rejectRelationship({
        optimisticResponse: {
          __typename: 'Mutation' as const,
          rejectDocumentRelationship: {
            __typename: 'DocumentRelationship' as const,
            childDocumentId: rel.childDocumentId ?? null,
            childRelationshipType: rel.childRelationshipType,
            id: rel.id,
            parentDocumentId: rel.parentDocumentId ?? null,
            review: currentUser
              ? {
                  __typename: 'DocumentRelationshipReview' as const,
                  outcome: Apollo.ReviewOutcome.Rejected,
                  reviewedBy: {
                    __typename: 'User' as const,
                    displayName: currentUser.displayName,
                    id: currentUser.id,
                  },
                }
              : null,
            source: rel.source,
          },
        },
        variables: { id: rel.id },
      });
      if (showUndo && data?.rejectDocumentRelationship) {
        dispatchUndoSnack(
          'Relationship rejected.',
          () => {
            void restoreRelationship({
              optimisticResponse: {
                __typename: 'Mutation' as const,
                restoreDocumentRelationship: {
                  __typename: 'DocumentRelationship' as const,
                  childDocumentId: rel.childDocumentId ?? null,
                  childRelationshipType: rel.childRelationshipType,
                  id: rel.id,
                  parentDocumentId: rel.parentDocumentId ?? null,
                  review: null,
                  source: rel.source,
                },
              },
              refetchQueries: [
                { query: Apollo.GetDocumentFileDetailsDocument, variables: { documentId } },
              ],
              variables: { id: rel.id },
            });
          },
          'info'
        );
      }
    },
    [currentUser, dispatchUndoSnack, rejectRelationship, restoreRelationship]
  );

  return { loading, reject };
}

export function useRestoreRelationship() {
  const [restoreRelationship, { loading }] = Apollo.useRestoreDocumentRelationshipMutation();

  const restore = useCallback(
    async (rel: RelationshipForMutation, documentId: string) => {
      await restoreRelationship({
        optimisticResponse: {
          __typename: 'Mutation' as const,
          restoreDocumentRelationship: {
            __typename: 'DocumentRelationship' as const,
            childDocumentId: rel.childDocumentId ?? null,
            childRelationshipType: rel.childRelationshipType,
            id: rel.id,
            parentDocumentId: rel.parentDocumentId ?? null,
            review: null,
            source: rel.source,
          },
        },
        refetchQueries: [
          { query: Apollo.GetDocumentFileDetailsDocument, variables: { documentId } },
        ],
        variables: { id: rel.id },
      });
    },
    [restoreRelationship]
  );

  return { loading, restore };
}

export function useUpdateRelationshipType() {
  const dispatchUndoSnack = useDispatchUndoSnack();
  const [updateRelationshipType, { loading }] = Apollo.useUpdateDocumentRelationshipTypeMutation();
  const updateType = useCallback(
    async (
      rel: RelationshipForMutation,
      newType: Apollo.ChildRelationshipType,
      previousType?: Apollo.ChildRelationshipType | null
    ) => {
      const makeOptimistic = (
        type: Apollo.ChildRelationshipType
      ): Apollo.UpdateDocumentRelationshipTypeMutation => ({
        __typename: 'Mutation' as const,
        updateDocumentRelationshipType: {
          __typename: 'DocumentRelationship' as const,
          childDocumentId: rel.childDocumentId ?? null,
          childRelationshipType: type,
          id: rel.id,
          parentDocumentId: rel.parentDocumentId ?? null,
          review: rel.review
            ? {
                __typename: 'DocumentRelationshipReview' as const,
                outcome: rel.review.outcome,
                reviewedBy: rel.review.reviewedBy,
              }
            : null,
          source: rel.source,
        },
      });
      const { data } = await updateRelationshipType({
        optimisticResponse: makeOptimistic(newType),
        variables: { childRelationshipType: newType, id: rel.id },
      });
      if (previousType && data?.updateDocumentRelationshipType) {
        dispatchUndoSnack(
          'Relationship type updated.',
          () => {
            void updateRelationshipType({
              optimisticResponse: makeOptimistic(previousType),
              variables: { childRelationshipType: previousType, id: rel.id },
            });
          },
          'info'
        );
      }
    },
    [dispatchUndoSnack, updateRelationshipType]
  );

  return { loading, updateType };
}

export function useSetParentAndConfirm() {
  const [setParent, { loading }] = Apollo.useSetDocumentRelationshipParentAndConfirmMutation();
  const setParentAndConfirm = useCallback(
    async (rel: RelationshipForMutation, newParentDocumentId: string) => {
      await setParent({
        variables: { id: rel.id, parentDocumentId: newParentDocumentId },
      });
    },
    [setParent]
  );

  return { loading, setParentAndConfirm };
}

export function useSetChildAndConfirm() {
  const [setChild, { loading }] = Apollo.useSetDocumentRelationshipChildAndConfirmMutation();
  const setChildAndConfirm = useCallback(
    async (rel: RelationshipForMutation, newChildDocumentId: string) => {
      await setChild({
        variables: { childDocumentId: newChildDocumentId, id: rel.id },
      });
    },
    [setChild]
  );

  return { loading, setChildAndConfirm };
}
